import { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import server from 'server';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  MenuItem,
  Select,
  FormControl,
  Chip,
  Paper,
  Button,
  Box
} from '@material-ui/core';
import { GridColumns, DataGrid, GridCellParams, GridSortDirection } from '@material-ui/data-grid';
import { Search } from 'components/Search';
import { useStyles } from './styles';
import RefreshIcon from '@material-ui/icons/Refresh';
import AppsIcon from '@material-ui/icons/Apps';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import VpnKeyIcon from '@material-ui/icons/VpnKey';
import ScheduleIcon from '@material-ui/icons/Schedule';
import { SamlStatusData } from './types';
import { ReactComponent as Logos } from 'assets/logos.svg';
import { useAppPreloader } from 'hooks/useAppPreloader';
const formatDate = (date: string | null | undefined) => {
  if (!date) return 'Never';

  const parsedDate = new Date(date);
  if (Number.isNaN(parsedDate.getTime())) return '-';

  return parsedDate.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
    timeZoneName: 'short',
  });
};

const SamlStatus = () => {
  const classes = useStyles();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('All');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const {
    data,
    isLoading,
    isError,
    refetch,
    isFetching,
  } = useQuery<SamlStatusData>(
    'samlStatus',
    () =>
      server.get('saml/status').then((response) => {
        return response.data?.data ?? response.data;
      }),
    { refetchInterval: 30000 }
  );
  useAppPreloader(isLoading);

  const providers = useMemo(() => data?.service_providers || [], [data]);
  const activeCount = providers.filter((item) => item.is_active).length;
  const inactiveCount = providers.length - activeCount;

  const filteredProviders = useMemo(() => {
    const rows = providers.map((item, index) => ({ ...item, id: item.uuid || index }));
    if (!search.trim()) return rows;
    const s = search.toLowerCase();
    return rows.filter((item) =>
      (item.app_name || '').toLowerCase().includes(s) ||
      (item.entity_id || '').toLowerCase().includes(s)
    );
  }, [providers, search]);

  const columns: GridColumns = [
    {
      field: 'app_name', headerName: 'APPLICATION NAME', flex: 1,
      renderHeader: () => <span style={{ fontWeight: 'bold', fontSize: 13 }}>APPLICATION NAME</span>,
      renderCell: (params: GridCellParams) => (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          minWidth: 0,
          width: '100%',
          gap: 2,
        }}>
          <div style={{
            fontWeight: 600,
            fontSize: 13,
            color: '#29344b',
            lineHeight: '18px',
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
          }}>
            {params.row.app_name || '-'}
          </div>

          <div style={{
            fontSize: 11,
            color: '#8993a6',
            lineHeight: '15px',
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
          }}>
            {params.row.uuid || '-'}
          </div>
        </div>
      ),
    },
    {
      field: 'entity_id', headerName: 'ENTITY ID', flex: 1.5,
      renderHeader: () => <span style={{ fontWeight: 'bold', fontSize: 13 }}>ENTITY ID</span>,
      renderCell: (params: GridCellParams) => (
        <span style={{ fontSize: 12, color: '#758197', wordBreak: 'break-all' }}>{params.row.entity_id || '-'}</span>
      ),
    },
    {
      field: 'is_active', headerName: 'STATUS', flex: 0.6,
      renderHeader: () => <span style={{ fontWeight: 'bold', fontSize: 13 }}>STATUS</span>,
      renderCell: (params: GridCellParams) => (
        <Chip
          size="small"
          label={params.row.is_active ? 'Active' : 'Inactive'}
          className={params.row.is_active ? classes.liveChip : classes.inactiveChip}
        />
      ),
    },
    {
      field: 'last_successful_authentication_at', headerName: 'LAST AUTH', flex: 1,
      renderHeader: () => <span style={{ fontWeight: 'bold', fontSize: 13 }}>LAST SUCCESSFUL AUTH</span>,
      renderCell: (params: GridCellParams) => (
        <span style={{ fontSize: 13 }}>{formatDate(params.row.last_successful_authentication_at)}</span>
      ),
    },
  ];


  if (isLoading || !data) return null;

  if (isError) {
    return (
      <div className={classes.root}>
        <Typography className={classes.error}>
          Unable to load SAML status. Please try again.
        </Typography>
        <Box mt={2}>
          <Button
            size="small"
            color="primary"
            variant="outlined"
            onClick={() => refetch()}
          >
            Retry
          </Button>
        </Box>
      </div>
    );
  }

  const isLive = (data.idp?.status || '').toLowerCase() === 'live';

  const stats = [
    {
      label: 'Total Service Providers',
      value: providers.length,
      icon: <AppsIcon />,
      background: '#eef2ff',
      color: '#5368d5',
    },
    {
      label: 'Active Service Providers',
      value: activeCount,
      icon: <CheckCircleIcon />,
      background: '#e8f7ee',
      color: '#19945a',
    },
    {
      label: 'Inactive Service Providers',
      value: inactiveCount,
      icon: <ErrorOutlineIcon />,
      background: '#fff1e8',
      color: '#d97736',
    },
    {
      label: 'Last Checked',
      value: formatDate(data.idp?.checked_at),
      icon: <ScheduleIcon />,
      background: '#f1edff',
      color: '#7657ce',
    },
  ];

  return (
    <div className={classes.root} style={{ flexGrow: 1, width: '100%' }}>
      {/* PAGE HEADER */}
      <div className={classes.pageHeader}>
        <div>
          <Typography component="div" className={classes.heading}>SAML Status</Typography>
          <Typography component="div" className={classes.subtitle}>
            Monitor Identity Provider health and Service Provider activity.
          </Typography>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Chip
            label={isFetching ? 'Refreshing...' : 'Status Overview'}
            size="small"
            variant="outlined"
            className={classes.neutralChip}
          />
          <Button
            className={classes.refreshButton}
            size="small"
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => refetch()}
            disabled={isFetching}
          >
            Refresh
          </Button>
        </div>
      </div>

      {/* IDENTITY PROVIDER OVERVIEW */}
      <Card className={`${classes.card} ${classes.idpCard}`}>
        <CardContent className={classes.cardContent}>
          <div className={classes.idpTop}>
            <div className={classes.idpIdentity}>
              <div className={classes.iconBox} style={{ marginRight: 12 }}>
                <Logos width={32} height={32} />
              </div>
              <div style={{ minWidth: 0 }}>
                <Typography className={classes.idpName}>
                  {data.idp?.name || 'SAML Identity Provider'}
                </Typography>
                <Typography className={classes.idpDescription}>
                  Identity Provider Overview
                </Typography>
              </div>
            </div>

            <Chip
              label={isLive ? 'LIVE' : (data.idp?.status || 'UNKNOWN').toUpperCase()}
              size="small"
              className={isLive ? classes.liveChip : classes.inactiveChip}
            />
          </div>

          {data.idp?.reason && (
            <Typography className={classes.reason}>
              {data.idp.reason}
            </Typography>
          )}

          <div className={classes.idpDetails}>
            <div>
              <Typography component="div" className={classes.infoLabel}>Since</Typography>
              <Typography component="div" className={classes.infoValue}>{formatDate(data.idp?.since)}</Typography>
              <Typography component="div" variant="caption" color="textSecondary">Basis: {data.idp?.since_basis || '-'}</Typography>
            </div>
            <div>
              <Typography component="div" className={classes.infoLabel}>Last Checked</Typography>
              <Typography component="div" className={classes.infoValue}>{formatDate(data.idp?.checked_at)}</Typography>
            </div>
            <div>
              <Typography component="div" className={classes.infoLabel}>Current Status</Typography>
              <Typography component="div" className={classes.infoValue}>{data.idp?.status || '-'}</Typography>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* SUMMARY CARDS */}
      <Grid container spacing={2} className={classes.statsGrid}>
        {stats.map((item) => (
          <Grid item xs={12} sm={6} md={3} key={item.label}>
            <Card className={classes.card}>
              <div className={classes.statCard}>
                <div className={classes.statIcon} style={{ background: item.background, color: item.color }}>
                  {item.icon}
                </div>
                <div style={{ minWidth: 0 }}>
                  <Typography component="div" className={classes.statLabel}>{item.label}</Typography>
                  <Typography component="div" className={classes.statValue}>{item.value}</Typography>
                </div>
              </div>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* LAST SUCCESSFUL AUTHENTICATION */}
      <div className={classes.section}>
        <div className={classes.sectionHeader}>
          <Typography component="div" className={classes.sectionTitle}>Last Successful Authentication</Typography>
          <Typography component="div" className={classes.sectionSubtitle}>Most recent successful SAML sign-in activity.</Typography>
        </div>

        <Card className={classes.card}>
          <CardContent className={classes.cardContent}>
            {data.last_successful_authentication ? (
              <>
                <div className={classes.authHeader}>
                  <div className={classes.authIcon}><VpnKeyIcon /></div>
                  <div style={{ minWidth: 0 }}>
                    <Typography component="div" className={classes.authName}>{data.last_successful_authentication.app_name || '-'}</Typography>
                    <Chip label="Successful" size="small" className={classes.liveChip} />
                  </div>
                </div>
                <div className={classes.authGrid}>
                  <div>
                    <Typography component="div" className={classes.infoLabel}>Authentication Time</Typography>
                    <Typography component="div" className={classes.infoValue}>{formatDate(data.last_successful_authentication.authenticated_at)}</Typography>
                  </div>
                  <div>
                    <Typography component="div" className={classes.infoLabel}>Service Provider UUID</Typography>
                    <Typography component="div" className={classes.infoValue}>{data.last_successful_authentication.service_provider_uuid || '-'}</Typography>
                  </div>
                  <div>
                    <Typography component="div" className={classes.infoLabel}>Entity ID</Typography>
                    <Typography component="div" className={classes.infoValue}>{data.last_successful_authentication.entity_id || '-'}</Typography>
                  </div>
                </div>
              </>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div className={classes.authIcon}><VpnKeyIcon /></div>
                <Typography variant="body2" color="textSecondary">No successful authentication found.</Typography>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* SERVICE PROVIDERS */}
      <div className={classes.section}>
        <div className={classes.sectionHeader}>
          <Typography component="div" className={classes.sectionTitle}>Service Providers</Typography>
          <Typography component="div" className={classes.sectionSubtitle}>Manage and monitor all registered SAML Service Providers.</Typography>
        </div>

        <Grid container justify='space-between' alignItems='center' style={{ marginBottom: 8 }}>
          <div style={{ width: 300 }}>
            <Search handelSearch={(f: string) => { setSearch(f); setPage(0); }} />
          </div>
          {/* <FormControl
            variant="outlined"
            size="small"
            className={classes.filter}
          >
            <Select
              value={filter}
              onChange={(event) => setFilter(event.target.value as string)}
            >
              <MenuItem value="All">All Status</MenuItem>
              <MenuItem value="Active">Active</MenuItem>
              <MenuItem value="Inactive">Inactive</MenuItem>
            </Select>
          </FormControl> */}
        </Grid>

        <Paper elevation={0} style={{ width: '100%', overflowX: 'auto' }}>
          <DataGrid
            autoHeight
            disableSelectionOnClick
            rows={filteredProviders}
            columns={columns}
            pageSize={pageSize}
            rowsPerPageOptions={[10, 25, 50]}
            pagination
            paginationMode='client'
            rowCount={filteredProviders.length}
            page={page}
            onPageChange={(p) => setPage(p.page)}
            onPageSizeChange={(p) => { setPageSize(p.pageSize); setPage(0); }}
            sortingOrder={['desc', 'asc']}
            sortModel={[{ field: 'app_name', sort: 'asc' as GridSortDirection }]}
            className={classes.noBorder}
          />
        </Paper>
      </div>
    </div>
  );
};

export default SamlStatus;
